import {
  NoConversionError,
  Unit,
  UnknownUnitError,
  findUnitConverter,
  isUnitEqual,
  parseUnitString,
  unitToString,
} from '../../unit';
import { ASTCall, ASTExpr } from '../ast';
import { evalExpression } from '../eval';
import { ExprType, stringifyType } from '../expr_types';
import { extractStringLiteral } from '../literal';
import { Quantity } from '../quantity';
import { exprToSql } from '../sql';
import {
  TypecheckWarning,
  assertArgShape,
  assertNoKwargs,
  assertNumeric,
  typecheckExpression,
} from '../typecheck';
import { Context, Func, SqlExpression } from '../types';

export const convertible: Func = {
  /**
   * convertible(value, "unit"): boolean
   */
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 2, 2);
    assertNoKwargs(call);

    // The first argument needs to be numeric.
    assertNumeric(ctx, call.args[0], warnings);

    // The second argument needs to be a known unit.
    // Get the value out of that argument.
    const unit = extractStringLiteral(call.args[1]);

    try {
      // If this throws, then we'll fail the typecheck.
      parseUnitString(unit);
    } catch (e) {
      if (e instanceof NoConversionError) {
        throw new UnknownUnitError(unit);
      }
      throw e;
    }

    return {
      ty: 'boolean',
    };
  },

  eval(ctx: Context, call: ASTCall): unknown {
    // Return true if the first argument can be converted into the second argument.
    const value = evalExpression(ctx, call.args[0]);
    const toUnit = parseUnitString(extractStringLiteral(call.args[1]));

    if (value === null) {
      return false;
    } else if (value instanceof Quantity) {
      return canConvertUnit(value.unit, toUnit);
    } else if (typeof value === 'number') {
      const exprType = typecheckExpression(ctx, call.args[0]);
      const baseType = exprType.ty === 'nullable' ? exprType.baseTy : exprType;
      if (baseType.ty !== 'number') {
        throw new Error(
          `Runtime type was number, but expr had type ${stringifyType(
            exprType
          )}`
        );
      }
      return !!baseType.unit ? canConvertUnit(baseType.unit, toUnit) : true;
    } else {
      throw new Error(
        `Unexpected runtime value ${value}, expected quantity or number`
      );
    }
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const toUnit = parseUnitString(extractStringLiteral(call.args[1]));
    return convertibleToSql(ctx, call.args[0], toUnit);
  },
};

function convertibleToSql(
  ctx: Context,
  expr: ASTExpr,
  unit: Unit
): SqlExpression {
  const eSql = exprToSql(ctx, expr);
  const exprType = typecheckExpression(ctx, expr);

  const baseType = exprType.ty === 'nullable' ? exprType.baseTy : exprType;

  const falseResult = {
    expr: `FALSE`,
    stage: eSql.stage,
    unitExpr: null,
    error: eSql.error,
    trace: null,
  };

  if (eSql.unitExpr) {
    if (baseType.ty !== 'quantity') {
      throw new Error(
        `internal error: expression had unitExpr (defined a unit column) but was not quantity type. Was: ${stringifyType(
          exprType
        )}`
      );
    }

    const validUnits: Array<Unit> = [];
    for (const eUnit of baseType.units) {
      // convert from potential units to expected output unit
      try {
        if (canConvertUnit(eUnit, unit)) {
          validUnits.push(eUnit);
        }
      } catch (e) {
        if (!(e instanceof NoConversionError)) {
          throw e;
        }
      }
    }

    // If there are no valid units, then we'll return false.
    if (!validUnits.length) {
      return falseResult;
    }

    // Values are expressions
    const unitExpr = eSql.unitExpr;

    const allowedUnitsText = validUnits
      .map((unit) => `'${unitToString(unit)}'`)
      .join(', ');

    return {
      expr: `${unitExpr} IN (${allowedUnitsText})`,
      stage: eSql.stage,
      unitExpr: null,
      error: eSql.error,
      trace: null,
    };
  } else {
    if (baseType.ty === 'quantity') {
      throw new Error(
        `internal error: Type was ${stringifyType(
          baseType
        )} but a unit column was not defined. (did not have unitExpr)`
      );
    } else if (baseType.ty !== 'number') {
      throw new Error(
        `internal error: Attempting to convert non-numeric type ${stringifyType(
          exprType
        )} to unit: ${JSON.stringify(unit)}`
      );
    }
    const trueResult = {
      expr: `TRUE`,
      stage: eSql.stage,
      unitExpr: null,
      error: eSql.error,
      trace: null,
    };

    // If the value doesn't have a unit, we'll treat it like `cast`, so return true
    if (!baseType.unit) {
      return trueResult;
    }

    // If the units are equal, there's nothing to do! The code below will behave
    // correctly, but we'll e.g., add an extra conversion to the TraceAST, which
    // makes the resulting trace more confusing. Best to just do nothing!
    if (isUnitEqual(baseType.unit, unit)) {
      return eSql;
    }

    return canConvertUnit(baseType.unit, unit) ? trueResult : falseResult;
  }
}

export function canConvertUnit(from: Unit, to: Unit): boolean {
  try {
    findUnitConverter(from, to);
    return true;
  } catch (e) {
    if (e instanceof NoConversionError) {
      return false;
    }
    throw e;
  }
}
